import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { ComparisonService } from '@/services/comparison-service';
import type { DateRange, ComparisonResult } from '@/types/comparison';

const comparisonService = new ComparisonService(db);

interface ComparisonStore {
  periodA: DateRange | null;
  periodB: DateRange | null;
  result: ComparisonResult | null;
  isLoading: boolean;
  error: string | null;
  setPeriodA: (range: DateRange) => void;
  setPeriodB: (range: DateRange) => void;
  loadComparison: (params: { waypointId: string } | { propertyId: string }) => Promise<void>;
  reset: () => void;
}

export const useComparisonStore = create<ComparisonStore>((set, get) => ({
  periodA: null,
  periodB: null,
  result: null,
  isLoading: false,
  error: null,

  setPeriodA: (range) => set({ periodA: range }),
  setPeriodB: (range) => set({ periodB: range }),

  loadComparison: async (params) => {
    const { periodA, periodB } = get();
    if (!periodA || !periodB) return;

    set({ isLoading: true, error: null });
    try {
      let result: ComparisonResult;
      if ('waypointId' in params) {
        result = await comparisonService.getComparisonData(params.waypointId, periodA, periodB);
      } else {
        result = await comparisonService.getPropertyComparisonData(
          params.propertyId,
          periodA,
          periodB,
        );
      }
      set({ result, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load comparison';
      console.error('ComparisonStore.loadComparison:', err);
      set({ error: message, isLoading: false });
    }
  },

  reset: () =>
    set({ periodA: null, periodB: null, result: null, isLoading: false, error: null }),
}));
